import { useDeviceGroupDetail } from "@/api/device-group-http-functions";
import { useDeviceDetail } from "@/api/device-http-functions";
import { Skeleton } from "@/components/ui/skeleton";
import { getDeviceLabel } from "@/utils/utils";
import React, {
    createContext,
    useCallback,
    use,
    useEffect,
    useState,
} from "react";
import { useLocation, useParams } from "react-router";

type BreadcrumbsContextType = {
    breadcrumbs: Record<string, React.ReactNode>;
    setBreadcrumb: (key: string, value: React.ReactNode) => void;
    clearBreadcrumbs: () => void;
};

export type BreadcrumbHandle = {
    breadcrumbDisplay?: string;
    breadcrumbKey?: string;
    breadCrumbUseParam?: boolean;
};

const BreadcrumbsContext = createContext<BreadcrumbsContextType | undefined>(
    undefined,
);

export const BreadcrumbsProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [breadcrumbs, setBreadcrumbs] = useState<
        Record<string, React.ReactNode>
    >({});
    const location = useLocation();

    const setBreadcrumb = useCallback(
        (key: string, value: React.ReactNode) => {
            setBreadcrumbs((prev) => ({ ...prev, [key]: value }));
        },
        [setBreadcrumbs],
    );

    const clearBreadcrumbs = () => {
        setBreadcrumbs({});
    };

    useEffect(() => {
        clearBreadcrumbs();
    }, [location.pathname]);

    return (
        <BreadcrumbsContext
            value={{ breadcrumbs, setBreadcrumb, clearBreadcrumbs }}
        >
            {children}
        </BreadcrumbsContext>
    );
};

export const useBreadcrumbs = (): BreadcrumbsContextType => {
    const context = use(BreadcrumbsContext);
    if (!context) {
        throw new Error(
            "useBreadcrumbs must be used within a BreadcrumbsProvider",
        );
    }
    return context;
};

export const BreadcrumbSetter = ({
    breadcrumbsToSet,
}: {
    breadcrumbsToSet: { breadcrumbKey: string; value: React.ReactNode }[];
}) => {
    const { setBreadcrumb, breadcrumbs } = useBreadcrumbs();

    useEffect(() => {
        for (const { breadcrumbKey, value } of breadcrumbsToSet) {
            const currentCrumb = breadcrumbs[breadcrumbKey];
            if (currentCrumb !== value) {
                setBreadcrumb(breadcrumbKey, value);
            }
        }
    }, [breadcrumbs, breadcrumbsToSet, setBreadcrumb]);

    return null;
};

export const DeviceGroupBreadcrumbSetter = () => {
    const { deviceGroupTag } = useParams<{
        deviceGroupTag: string;
    }>();
    const { data: deviceGroup, isLoading } = useDeviceGroupDetail(
        deviceGroupTag!,
    );
    return (
        <BreadcrumbSetter
            breadcrumbsToSet={[
                ...(deviceGroupTag
                    ? [
                          {
                              breadcrumbKey: ":deviceGroupTag",
                              value: isLoading ? (
                                  <Skeleton className="h-5 w-16" />
                              ) : (
                                  (deviceGroup?.deviceGroupName ??
                                  deviceGroupTag)
                              ),
                          },
                      ]
                    : []),
            ]}
        />
    );
};

export const DeviceBreadcrumbSetter = () => {
    const { deviceIdentity, deviceGroupTag } = useParams<{
        deviceIdentity: string;
        deviceGroupTag: string;
    }>();
    const { data: device, isLoading: isDeviceLoading } = useDeviceDetail(
        deviceIdentity!,
        undefined,
        { query: { enabled: !!deviceIdentity } },
    );
    return (
        <BreadcrumbSetter
            breadcrumbsToSet={[
                // Only claim the ":deviceGroupTag" crumb when there's no
                // ancestor device-group/site/schedule route already setting
                // it via DeviceGroupBreadcrumbSetter — otherwise the two
                // setters race to overwrite each other's value.
                ...(!deviceGroupTag && device?.siteName
                    ? [
                          {
                              breadcrumbKey: ":deviceGroupTag",
                              value: isDeviceLoading ? (
                                  <Skeleton className="h-5 w-16" />
                              ) : (
                                  device?.siteName
                              ),
                          },
                      ]
                    : []),
                {
                    breadcrumbKey: ":deviceIdentity",
                    value: isDeviceLoading ? (
                        <Skeleton className="h-5 w-16" />
                    ) : device ? (
                        getDeviceLabel(device)
                    ) : undefined,
                },
            ]}
        />
    );
};
